import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import {
    Merchant,
    BankInfoResponse,
    Charge,
    ChargeById,
    TransactionStats,
} from '../../types';
import EditIcon from '@mui/icons-material/Edit';
import {
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Box,
    IconButton,
    Button,
} from '@mui/material';
import {
    StyledRoot,
    StyledCard,
    StyledContentBox,
    StyledLeftColumn,
    StyledMerchantGrid,
    StyledMerchantItem,
    StyledRightColumn,
} from './MerchantById.styles';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useToast } from '../../providers/ToastContext';
import ChargeModal from '../ChargeForm/ChargeModal';
import ChargesForm from '../ChargeForm';
import MerchantTransactionStatsChart from '../MerchantChart/MerchantTransactionStatsChart';

import GenerateReportModal from '../MerchantReport/GenerateReportModal';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MerchantTransactionLineChart from '../MerchantChart/MerchantTransactionLineChart';

const MerchantById: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const { id } = useParams<{ id: string }>();
    const [merchant, setMerchant] = useState<Merchant | null>(null);
    const [charges, setCharges] = useState<Charge[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedBankIndex, setSelectedBankIndex] = useState(0);
    const [selectedCharge, setSelectedCharge] = useState<ChargeById | null>(
        null
    );
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<number>(
        new Date().getMonth() + 1
    );
    const [selectedYear, setSelectedYear] = useState<number>(
        new Date().getFullYear()
    );

    const handleDeleteCharge = async (chargeId: number, index: number) => {
        if (!chargeId) return;
        const id = Number(chargeId);
        try {
            const response = await apiService.deleteCharge(id);
            if (response.data?.affected === 1) {
                setCharges(prevCharges =>
                    prevCharges.filter((_, i) => i !== index)
                );
                showToast('Charge deleted sucessfully', 'success');
            } else {
                setError(response.msg || 'Error deleting charges');
                showToast('Error deleting charges', 'error');
            }
        } catch (err) {
            setError('Unexpected error occurred while deleting charge');
            showToast(
                'Unexpected error occurred while deleting charge',
                'error'
            );
        }
    };

    const handleEditCharge = async (chargeId: number) => {
        if (!chargeId) return;

        try {
            const response = await apiService.getChargeById(chargeId);
            if (response.data) {
                setSelectedCharge(response.data);
                setIsEditModalOpen(true);
                showToast('Charge details fetched successfully', 'success');
            } else {
                setError('Charge not found');
                showToast('Charge not found', 'error');
            }
        } catch (err) {
            console.error('Error fetching charge:', err);
            setError('Unexpected error occurred while fetching charge');
            showToast(
                'Unexpected error occurred while fetching charge',
                'error'
            );
        }
    };

    const handleEditBankDetails = async (bankId: number) => {};

    const fetchData = async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            // Fetch merchant data
            const merchantResponse = await apiService.getMerchantById(
                Number(id)
            );
            if (merchantResponse.data?.length > 0) {
                const merchantData = merchantResponse.data[0];

                setMerchant(merchantData);

                // Extract and set charges if available
                if (
                    'charges' in merchantData &&
                    merchantData.charges &&
                    Array.isArray(merchantData.charges)
                ) {
                    setCharges(merchantData.charges);
                }
            } else {
                setError('No merchant data found');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Unexpected error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [id]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box mt={4}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!merchant) {
        return (
            <Box mt={4}>
                <Alert severity="info">No merchant details found.</Alert>
            </Box>
        );
    }

    const merchantData = [
        { label: 'Business Name', value: merchant.name },
        { label: 'DBA', value: merchant.dba },
        { label: 'Email', value: merchant.email },
        { label: 'URL', value: merchant.url },
        { label: 'Address', value: merchant.address },
        { label: 'City', value: merchant.city },
        { label: 'State', value: merchant.state },
        { label: 'Zip Code', value: merchant.zipCode },
        { label: 'Status', value: merchant.status },
        { label: 'Start Date', value: merchant.startDate },
        { label: 'MPA Start Date', value: merchant.mpaStartDate },
        { label: 'Termination Date', value: merchant.terminationDate || 'N/A' },
        { label: 'Capacity', value: merchant.capacity },
        { label: 'Persona', value: merchant.persona },
        { label: 'MPA', value: merchant.mpa },
        { label: 'EIN', value: merchant.ein ? merchant.ein.toString() : 'N/A' },
        { label: 'Merchant Code', value: merchant.merchantCode || 'N/A' },
        {
            label: 'Created At',
            value: merchant.createdAt
                ? new Date(merchant.createdAt).toLocaleString()
                : 'N/A',
        },
        {
            label: 'Last Updated',
            value: merchant.updatedAt
                ? new Date(merchant.updatedAt).toLocaleString()
                : 'N/A',
        },
    ];
    // Function to get bank data for display
    const getBankData = (bank: BankInfoResponse) => [
        { label: 'Account Holder Name', value: bank.name },
        { label: 'Email', value: bank.email },
        { label: 'Phone', value: `${bank.countryCode || ''} ${bank.phone}` },
        { label: 'City', value: bank.city },
        { label: 'State', value: bank.state },
        { label: 'Zip Code', value: bank.zipcode },
        { label: 'Account Number', value: bank.accountNumber },
        { label: 'Account Type', value: bank.accountType },
        { label: 'Routing Number', value: bank.routingNumber },
        { label: 'Transmission Type', value: bank.transmissionType },
        { label: 'Wires', value: bank.wires },
        { label: 'IBAN Number', value: bank.ibanNumber || 'N/A' },
        { label: 'SWIFT Code', value: bank.swiftCode || 'N/A' },
        { label: 'Status', value: bank.status || 'N/A' },
    ];

    const formatChargeData = (charge: { [key: string]: string | number }) => {
        return Object.entries(charge).map(([key, value]) => ({
            key,
            value:
                value !== null && value !== undefined
                    ? value.toString()
                    : 'N/A',
        }));
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedCharge(null);
    };

    const handleUpdateSuccess = () => {
        showToast('Charge updated successfully!', 'success');
        setIsEditModalOpen(false);
        setSelectedCharge(null);
        // Refresh charges data
        fetchData();
    };

    const handleOpenReportModal = () => {
        setIsReportModalOpen(true);
    };

    const handleCloseReportModal = () => {
        setIsReportModalOpen(false);
    };

    const handleGenerateReport = () => {
        showToast(
            `Generating report for ${selectedMonth}/${selectedYear}`,
            'info'
        );
        // Example: You could navigate or call API here
        // navigate(`/generateReport/${id}?month=${selectedMonth}&year=${selectedYear}`);
        setIsReportModalOpen(false);
    };

    return (
        <StyledRoot>
            <StyledCard>
                <CardContent>
                    <Box
                        sx={{
                            width: '100%',
                            mb: 6,
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                        }}
                    >
                        <MerchantTransactionStatsChart
                            data={merchant?.stats?.transactionstats}
                            loading={loading}
                        />
                        <MerchantTransactionLineChart
                            data={merchant.totaldata}
                        />
                    </Box>
                    {/* View Transactions Button - BELOW NAVBAR */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            flexWrap: 'wrap',
                            mb: 3,
                        }}
                        gap={2}
                    >
                        <Button
                            variant="contained"
                            startIcon={<SummarizeIcon />}
                            onClick={handleOpenReportModal}
                            sx={{
                                minWidth: { xs: '100%', sm: 'auto' },
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                            }}
                        >
                            Generate Monthly Report
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<ReceiptLongIcon />}
                            onClick={() => navigate(`/viewTransaction/${id}`)}
                            sx={{
                                minWidth: { xs: '100%', sm: 'auto' },
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                            }}
                        >
                            View Transactions
                        </Button>
                    </Box>

                    <Box sx={{ width: '100%', mb: 4 }}>
                        {charges && charges.length > 0 ? (
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold', mb: 2 }}
                                >
                                    Charges
                                </Typography>

                                {/* Grid layout instead of scrolling */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(300px, 1fr))',
                                        gap: 2,
                                    }}
                                >
                                    {charges.map((charge, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                height: '100%',
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'background.paper',
                                                boxShadow: 1,
                                                position: 'relative',
                                                transition: '0.2s',
                                                '&:hover': {
                                                    boxShadow: 3,
                                                },
                                            }}
                                        >
                                            {/* Edit & Delete icons */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    aria-label="edit charge"
                                                    onClick={() =>
                                                        handleEditCharge(
                                                            charge.id
                                                        )
                                                    }
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label="delete charge"
                                                    onClick={() =>
                                                        handleDeleteCharge(
                                                            charge.id,
                                                            index
                                                        )
                                                    }
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1.5,
                                                }}
                                            >
                                                {formatChargeData(
                                                    charge.newcharges
                                                ).map((item, itemIndex) => (
                                                    <Box key={itemIndex}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                            color="textSecondary"
                                                        >
                                                            {item.key
                                                                .replace(
                                                                    /([A-Z])/g,
                                                                    ' $1'
                                                                )
                                                                .trim()}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mt: 0.5,
                                                                fontWeight:
                                                                    'medium',
                                                            }}
                                                        >
                                                            {item.value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No charges available
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <StyledContentBox>
                        <StyledLeftColumn>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                Merchant Information
                            </Typography>
                            <StyledMerchantGrid>
                                {merchantData.map((item, index) => (
                                    <StyledMerchantItem key={index}>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {item.label}
                                        </Typography>
                                        {/* {item.label === 'URL' && item.value ? ( */}
                                        {(item.label === 'MPA' ||
                                            item.label === 'Persona' ||
                                            item.label === 'URL') &&
                                        item.value ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5 }}
                                            >
                                                <a
                                                    href={String(item.value)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#1976d2',
                                                        textDecoration: 'none',
                                                    }}
                                                >
                                                    {item.value}
                                                </a>
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5 }}
                                            >
                                                {item.value}
                                            </Typography>
                                        )}
                                    </StyledMerchantItem>
                                ))}
                            </StyledMerchantGrid>
                        </StyledLeftColumn>
                        <StyledRightColumn>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                Bank Details
                            </Typography>
                            {merchant.bankDetails &&
                            merchant.bankDetails.length > 0 ? (
                                merchant.bankDetails.map(
                                    (bank: BankInfoResponse, index: number) => {
                                        const currentBankData =
                                            getBankData(bank);
                                        return (
                                            <Box
                                                key={index}
                                                mb={
                                                    index <
                                                    merchant.bankDetails!
                                                        .length -
                                                        1
                                                        ? 3
                                                        : 0
                                                }
                                            >
                                                {merchant.bankDetails!.length >
                                                    1 && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            gutterBottom
                                                            sx={{
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            Bank Account{' '}
                                                            {index + 1}
                                                        </Typography>
                                                        <Box>
                                                            <IconButton
                                                                size="small"
                                                                aria-label="edit bank details"
                                                                // onClick={() =>
                                                                //     handleEditBankDetails(
                                                                //         merchant
                                                                //             ?.bankDetails
                                                                //             ?.id
                                                                //     )
                                                                // }
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                aria-label="delete charge"
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                )}
                                                <StyledMerchantGrid>
                                                    {currentBankData.map(
                                                        (item, idx) => (
                                                            <StyledMerchantItem
                                                                key={idx}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                    }}
                                                                >
                                                                    {item.value}
                                                                </Typography>
                                                            </StyledMerchantItem>
                                                        )
                                                    )}
                                                </StyledMerchantGrid>
                                            </Box>
                                        );
                                    }
                                )
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No banking information available
                                </Typography>
                            )}
                        </StyledRightColumn>
                    </StyledContentBox>
                    {/* Edit Charge Modal */}
                    <ChargeModal
                        open={isEditModalOpen}
                        onClose={handleCloseEditModal}
                        title="Edit Charge"
                    >
                        <ChargesForm
                            mode="edit"
                            chargeData={selectedCharge}
                            onSuccess={handleUpdateSuccess}
                            onCancel={handleCloseEditModal}
                            isModal={true}
                        />
                    </ChargeModal>
                    <GenerateReportModal
                        open={isReportModalOpen}
                        onClose={handleCloseReportModal}
                        onGenerate={(month, year) => {
                            showToast(
                                `Generating report for ${month}/${year}`,
                                'info'
                            );
                            // Example:
                            navigate(
                                `/generateReport/${id}?month=${month}&year=${year}`
                            );
                            setIsReportModalOpen(false);
                        }}
                    />
                </CardContent>
            </StyledCard>
        </StyledRoot>
    );
};

export default MerchantById;
